import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { sitePhotoGalleryService } from '../../../services/sitePhotoGalleryService';


// ─── Photo Card ─────────────────────────────────────────────────────────────
const PhotoCard = ({ photo, onClick }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!photo?.filePath) return;
      const { url, error: urlErr } = await sitePhotoGalleryService?.getSignedUrlForPhoto(photo);
      if (urlErr) {
        console.error('[PhotosTab] signed URL failed:', { photoId: photo?.id, code: urlErr?.code, message: urlErr?.message });
      }
      if (!cancelled) {
        setImgUrl(url);
        setImgLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [photo?.filePath, photo?.bucket]);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const sourceColors = {
    task: 'bg-blue-100 text-blue-700',
    snag: 'bg-amber-100 text-amber-700',
    incident: 'bg-red-100 text-red-700',
    document: 'bg-purple-100 text-purple-700',
  };

  return (
    <div
      className="group relative bg-card border border-border rounded-xl overflow-hidden cursor-pointer hover:shadow-elevation-2 transition-all hover:-translate-y-0.5"
      onClick={() => onClick?.(photo, imgUrl)}
    >
      <div className="aspect-[4/3] bg-muted overflow-hidden relative">
        {imgLoading && (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-border border-t-accent rounded-full animate-spin" />
          </div>
        )}
        {imgError && (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <Icon name="ImageOff" size={24} />
            <span className="text-xs">Failed to load</span>
          </div>
        )}
        {imgUrl && !imgError && (
          <img
            src={imgUrl}
            alt={`${photo?.sourceLabel} photo: ${photo?.sourceName}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Icon name="ZoomIn" size={18} className="text-foreground" />
          </div>
        </div>
        {/* Source badge */}
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${sourceColors?.[photo?.sourceType] || 'bg-muted text-muted-foreground'}`}>
            {photo?.sourceLabel}
          </span>
        </div>
      </div>
      <div className="p-3">
        <p className="text-xs font-semibold text-foreground truncate">{photo?.sourceName}</p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-muted-foreground truncate">{photo?.uploaderName}</span>
          <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">{formatDate(photo?.createdAt)}</span>
        </div>
      </div>
    </div>
  );
};

// ─── Photo Detail Modal ──────────────────────────────────────────────────────
const PhotoDetailModal = ({ photo, onClose }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoomed, setZoomed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!photo?.filePath) return;
    const load = async () => {
      setLoading(true);
      const { url, error: urlErr } = await sitePhotoGalleryService?.getSignedUrlForPhoto(photo);
      if (urlErr) {
        console.error('[PhotosTab] signed URL failed:', { photoId: photo?.id, code: urlErr?.code, message: urlErr?.message });
      }
      setImgUrl(url);
      setLoading(false);
    };
    load();
  }, [photo?.filePath, photo?.bucket]);

  const handleDownload = async () => {
    if (!imgUrl) return;
    setDownloading(true);
    try {
      const response = await fetch(imgUrl);
      const blob = await response?.blob();
      const url = URL?.createObjectURL(blob);
      const a = document?.createElement('a');
      a.href = url;
      a.download = photo?.fileName || 'photo.jpg';
      document?.body?.appendChild(a);
      a?.click();
      document?.body?.removeChild(a);
      URL?.revokeObjectURL(url);
    } catch {
      // silently fail
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return 'Unknown';
    return new Date(iso)?.toLocaleString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const sourceColors = {
    task: 'bg-blue-100 text-blue-700',
    snag: 'bg-amber-100 text-amber-700',
    incident: 'bg-red-100 text-red-700',
    document: 'bg-purple-100 text-purple-700',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div
        className="bg-card rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="min-w-0 flex items-center gap-3">
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${sourceColors?.[photo?.sourceType] || 'bg-muted text-muted-foreground'}`}>
              {photo?.sourceLabel}
            </span>
            <div className="min-w-0">
              <h3 className="font-semibold text-foreground truncate">{photo?.sourceName}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{photo?.uploaderName} · {formatDate(photo?.createdAt)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
            <button
              onClick={handleDownload}
              disabled={downloading || !imgUrl}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-accent-foreground rounded-lg text-xs font-semibold hover:bg-accent/90 transition-colors disabled:opacity-60"
            >
              {downloading
                ? <div className="w-3 h-3 border border-accent-foreground/40 border-t-accent-foreground rounded-full animate-spin" />
                : <Icon name="Download" size={13} />}
              Download
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center">
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Image */}
        <div
          className={`relative flex-1 overflow-hidden bg-black flex items-center justify-center min-h-0 ${zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
          onClick={() => setZoomed(!zoomed)}
        >
          {loading && (
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              <span className="text-white/60 text-sm">Loading...</span>
            </div>
          )}
          {imgUrl && (
            <img
              src={imgUrl}
              alt={`${photo?.sourceLabel}: ${photo?.sourceName}`}
              className={`max-h-full transition-transform duration-300 ${zoomed ? 'scale-150 max-w-none' : 'max-w-full object-contain'}`}
            />
          )}
          <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-lg pointer-events-none">
            {zoomed ? 'Click to zoom out' : 'Click to zoom in'}
          </div>
        </div>

        {/* Metadata */}
        <div className="px-5 py-4 border-t border-border flex-shrink-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Uploaded by</p>
              <p className="text-sm font-medium text-foreground mt-0.5">{photo?.uploaderName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Date & Time</p>
              <p className="text-sm font-medium text-foreground mt-0.5">{formatDate(photo?.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Related {photo?.sourceLabel}</p>
              <p className="text-sm font-medium text-foreground mt-0.5 truncate">{photo?.sourceName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">File Size</p>
              <p className="text-sm font-medium text-foreground mt-0.5">{formatSize(photo?.fileSize)}</p>
            </div>
          </div>
          {photo?.caption && (
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground">Notes</p>
              <p className="text-sm text-foreground mt-0.5">{photo?.caption}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Photos Tab ──────────────────────────────────────────────────────────────
const PhotosTab = ({ siteId, snags = [], incidents = [] }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [filters, setFilters] = useState({
    uploadedBy: 'all',
    dateFrom: '',
    dateTo: '',
    taskId: 'all',
    snagId: 'all',
    incidentId: 'all',
  });

  const loadPhotos = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError('');
    try {
      const result = await sitePhotoGalleryService?.getPhotosBySite(siteId, filters);
      if (result?.error) throw result?.error;
      const data = result?.data || [];
      setPhotos(data);

      // Extract unique workers
      const workerMap = new Map();
      data?.forEach(p => {
        if (p?.uploadedBy && p?.uploaderName) {
          workerMap?.set(p?.uploadedBy, { id: p?.uploadedBy, full_name: p?.uploaderName });
        }
      });
      setWorkers(Array.from(workerMap?.values()));

      // Extract unique tasks
      const taskMap = new Map();
      data?.forEach(p => {
        if (p?.taskId && p?.task?.title) {
          taskMap?.set(p?.taskId, { id: p?.taskId, title: p?.task?.title });
        }
      });
      setTasks(Array.from(taskMap?.values()));
    } catch (err) {
      console.error('[PhotosTab] load photos failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading photos: ${err?.message}`);
      } else {
        setError(`Failed to load photos: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, filters]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ uploadedBy: 'all', dateFrom: '', dateTo: '', taskId: 'all', snagId: 'all', incidentId: 'all' });
  };

  const hasActiveFilters = filters?.uploadedBy !== 'all' || filters?.dateFrom || filters?.dateTo ||
    filters?.taskId !== 'all' || filters?.snagId !== 'all' || filters?.incidentId !== 'all';

  const counts = useMemo(() => ({
    total: photos?.length,
    task: photos?.filter(p => p?.sourceType === 'task')?.length,
    snag: photos?.filter(p => p?.sourceType === 'snag')?.length,
    incident: photos?.filter(p => p?.sourceType === 'incident')?.length,
  }), [photos]);

  return (
    <div>
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total Photos', value: counts?.total, icon: 'Images', color: 'text-accent' },
          { label: 'From Tasks', value: counts?.task, icon: 'CheckSquare', color: 'text-blue-600' },
          { label: 'From Snags', value: counts?.snag, icon: 'AlertCircle', color: 'text-amber-600' },
          { label: 'From Incidents', value: counts?.incident, icon: 'AlertTriangle', color: 'text-red-600' },
        ]?.map(stat => (
          <div key={stat?.label} className="bg-muted/50 border border-border rounded-xl p-3 flex items-center gap-3">
            <div className="w-9 h-9 bg-card rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name={stat?.icon} size={18} className={stat?.color} />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground leading-none">{loading ? '—' : stat?.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{stat?.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="SlidersHorizontal" size={15} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Filter Photos</h3>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="ml-auto text-xs text-muted-foreground hover:text-foreground underline">
              Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Date from */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">From Date</label>
            <input
              type="date"
              value={filters?.dateFrom}
              onChange={e => handleFilterChange('dateFrom', e?.target?.value)}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/30 text-foreground"
            />
          </div>
          {/* Date to */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">To Date</label>
            <input
              type="date"
              value={filters?.dateTo}
              onChange={e => handleFilterChange('dateTo', e?.target?.value)}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/30 text-foreground"
            />
          </div>
          {/* Uploaded by */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Uploaded By</label>
            <select
              value={filters?.uploadedBy}
              onChange={e => handleFilterChange('uploadedBy', e?.target?.value)}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/30 text-foreground"
            >
              <option value="all">All Users</option>
              {workers?.map(w => <option key={w?.id} value={w?.id}>{w?.full_name}</option>)}
            </select>
          </div>
          {/* Task filter */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Task</label>
            <select
              value={filters?.taskId}
              onChange={e => handleFilterChange('taskId', e?.target?.value)}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/30 text-foreground"
            >
              <option value="all">All Tasks</option>
              {tasks?.map(t => <option key={t?.id} value={t?.id}>{t?.title}</option>)}
            </select>
          </div>
          {/* Snag filter */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Snag</label>
            <select
              value={filters?.snagId}
              onChange={e => handleFilterChange('snagId', e?.target?.value)}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/30 text-foreground"
            >
              <option value="all">All Snags</option>
              {snags?.map(s => <option key={s?.id} value={s?.id}>{s?.title}</option>)}
            </select>
          </div>
          {/* Incident filter */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Incident</label>
            <select
              value={filters?.incidentId}
              onChange={e => handleFilterChange('incidentId', e?.target?.value)}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/30 text-foreground"
            >
              <option value="all">All Incidents</option>
              {incidents?.map(i => <option key={i?.id} value={i?.id}>{i?.incident_type?.replace(/_/g, ' ') || 'Incident'}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Count + refresh */}
      {!loading && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-muted-foreground">
            {photos?.length} photo{photos?.length !== 1 ? 's' : ''}
          </p>
          <button
            onClick={loadPhotos}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="RefreshCw" size={12} /> Refresh
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
          <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={loadPhotos} className="ml-auto text-xs text-red-600 underline">Retry</button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 })?.map((_, i) => (
            <div key={i} className="bg-muted rounded-xl overflow-hidden animate-pulse">
              <div className="aspect-[4/3] bg-muted/70" />
              <div className="p-3 space-y-2">
                <div className="h-3 bg-muted/70 rounded w-3/4" />
                <div className="h-3 bg-muted/70 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : photos?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mb-4">
            <Icon name="Images" size={28} className="text-muted-foreground" />
          </div>
          <h3 className="text-base font-semibold text-foreground">No photos yet</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">
            Photos uploaded via tasks, snags, and incidents will appear here automatically.
          </p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="mt-3 text-sm text-accent underline">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos?.map(photo => (
            <PhotoCard key={photo?.id} photo={photo} onClick={(p, url) => setSelectedPhoto(p)} />
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selectedPhoto && (
        <PhotoDetailModal
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </div>
  );
};

export default PhotosTab;
